import { tns } from 'tiny-slider/src/tiny-slider';

class LogosSlider {
	constructor(el) {
		this.el = el;

		if (!this.el._logosMountParent) {
			this.el._logosMountParent = this.el.parentNode;
			this.el._logosMountNext = this.el.nextSibling;
		}

		if (!this.el.dataset.logosSliderOriginal) {
			this.el.dataset.logosSliderOriginal = this.el.innerHTML;
		}

		this._lastMode = null;
		this._lastDisplay = null;

		this._reinitRaf = null;
		this._isReiniting = false;
		this._pendingReinit = false;
		this._resizeAttached = false;
		this.handleResize = this.debounce(this.onResize.bind(this), 150);

		if (this.el.dataset.logosSliderInstance === '1') return;
		this.el.dataset.logosSliderInstance = '1';

		if (this.el._logosSliderCleanup && typeof this.el._logosSliderCleanup === 'function') {
			this.el._logosSliderCleanup();
		}

		this._mq = window.matchMedia('(max-width: 640.98px)');

		this._onResize = () => this.handleResize();
		this._onMqChange = () => this.handleResize();

		window.addEventListener('resize', this._onResize);
		this._resizeAttached = true;
		this._mq.addEventListener('change', this._onMqChange);

		this.el._logosSliderCleanup = () => {
			if (this._reinitRaf) cancelAnimationFrame(this._reinitRaf);
			if (this._resizeAttached) window.removeEventListener('resize', this._onResize);
			this._resizeAttached = false;
			this._mq.removeEventListener('change', this._onMqChange);
		};

		this.reinitIfNeeded(true);
	}

	debounce(fn, delay) {
		let timeout;
		return (...args) => {
			clearTimeout(timeout);
			timeout = setTimeout(() => fn.apply(this, args), delay);
		};
	}

	onResize() {
		this.scheduleReinit();
	}

	scheduleReinit(force = false) {
		if (this._isReiniting) {
			this._pendingReinit = true;
			return;
		}

		if (this._reinitRaf) cancelAnimationFrame(this._reinitRaf);
		this._reinitRaf = requestAnimationFrame(() => {
			this.reinitIfNeeded(force);
		});
	}

	getViewportWidth() {
		return document.documentElement.clientWidth;
	}

	getMode() {
		return this._mq && this._mq.matches ? 'mobile' : 'desktop';
	}

	getDisplayType() {
		return this.el.getAttribute('data-logos-display') || 'unknown';
	}

	getSlideCount() {
		const count = parseInt(this.el.dataset.logosCount, 10);
		if (Number.isFinite(count) && count >= 0) {
			return count;
		}

		return this.el.querySelectorAll('.block-logos__logo-slide').length;
	}

	getMaxItems(displayType, mode) {
		if (displayType === 'slider_three') return 3;
		if (displayType === 'slider_with_content') return 4;
		if (displayType === 'columns') return mode === 'mobile' ? 6 : 6;
		return 6;
	}

	shouldCenterLogos(displayType, mode) {
		if (this.el.dataset.logosCenter !== '1') return false;

		const count = this.getSlideCount();
		return count > 0 && count < this.getMaxItems(displayType, mode);
	}

	getSliderConfig(displayType, mode) {
		const slideCount = this.getSlideCount();
		const center = this.shouldCenterLogos(displayType, mode);

		if (displayType === 'slider_three') {
			if (center) {
				return {
					responsive: {
						350: { items: Math.min(slideCount, 1) },
						500: { items: Math.min(slideCount, 2) },
						700: { items: Math.min(slideCount, 3) }
					},
					center: true,
					gutter: 30
				};
			}

			return {
				responsive: {
					350: { items: 1 },
					500: { items: 2 },
					700: { items: 3 }
				},
				center: false,
				gutter: 30
			};
		}

		if (displayType === 'slider_with_content') {
			return {
				items: center ? Math.min(slideCount, 4) : 1,
				center,
				gutter: 0
			};
		}

		if (center) {
			return {
				responsive: {
					350: { items: Math.min(slideCount, 2) },
					500: { items: Math.min(slideCount, 3) },
					700: { items: Math.min(slideCount, 6) }
				},
				center: true,
				gutter: 20
			};
		}

		return {
			responsive: {
				350: { items: 2 },
				500: { items: 3 },
				700: { items: 6 }
			},
			center: false,
			gutter: 20
		};
	}

	getMobilePageSize(displayType) {
		if (displayType === 'slider_with_content') return 4;
		if (displayType === 'slider_three') return 3;
		return 6;
	}

	shouldInitSlider(mode, displayType) {
		if (displayType === 'slider') return true;
		if (displayType === 'slider_three') return true;
		if (displayType === 'slider_with_content') return true;
		if (displayType === 'columns') return mode === 'mobile';
		return false;
	}

	reinitIfNeeded(force = false) {
		const mode = this.getMode();
		const displayType = this.getDisplayType();

		if (!force && mode === this._lastMode && displayType === this._lastDisplay) {
			return;
		}

		this._lastMode = mode;
		this._lastDisplay = displayType;

		this.reinit(mode, displayType);
	}

	resetDom() {
		const fingerprint = this.el.dataset.logosSliderOriginal || null;
		const mountParent = this.el._logosMountParent;

		if (this.el._logosTns && typeof this.el._logosTns.destroy === 'function') {
			try {
				this.el._logosTns.destroy();
			} catch (e) {}
			this.el._logosTns = null;
		}

		if (this.el._logosLastOuter && this.el._logosLastOuter.parentNode) {
			const outer = this.el._logosLastOuter;
			const parent = outer.parentNode;

			if (outer.contains(this.el)) {
				parent.insertBefore(this.el, outer);
			}
			parent.removeChild(outer);
		}
		this.el._logosLastOuter = null;

		const outer2 = this.el.closest('.tns-outer');
		if (outer2 && outer2.parentNode) {
			const parent2 = outer2.parentNode;
			parent2.insertBefore(this.el, outer2);
			parent2.removeChild(outer2);
		}

		const mountNext = this.el._logosMountNext;

		if (mountParent && !mountParent.contains(this.el)) {
			try {
				if (mountNext && mountParent.contains(mountNext)) {
					mountParent.insertBefore(this.el, mountNext);
				} else {
					mountParent.appendChild(this.el);
				}
			} catch (e) {}
		}

		if (fingerprint && mountParent) {
			const dupContainers = mountParent.querySelectorAll('.block-logos__logos-slider[data-logos-slider-original]');

			dupContainers.forEach((node) => {
				if (node === this.el) return;
				if (node.dataset.logosSliderOriginal !== fingerprint) return;

				const w = node.closest('.tns-outer');
				if (w && w.parentNode) {
					w.parentNode.removeChild(w);
					return;
				}

				if (node.parentNode) {
					node.parentNode.removeChild(node);
				}
			});

			const outers = mountParent.querySelectorAll('.tns-outer');
			outers.forEach((w) => {
				if (!w.querySelector('.block-logos__logos-slider')) {
					if (w.parentNode) w.parentNode.removeChild(w);
				}
			});
		}

		this.el.classList.remove(
			'tns-slider',
			'tns-carousel',
			'tns-subpixel',
			'tns-calc',
			'tns-horizontal'
		);
		this.el.removeAttribute('style');
		this.el.removeAttribute('id');

		this.el.innerHTML = fingerprint || this.el.dataset.logosSliderOriginal;
	}

	reinit(mode, displayType) {
		this._isReiniting = true;
		this._pendingReinit = false;

		const prevVis = this.el.style.visibility;
		this.el.style.visibility = 'hidden';

		this.resetDom();

		if (!this.shouldInitSlider(mode, displayType)) {
			this.el.style.visibility = prevVis || '';
			this._isReiniting = false;

			if (this._pendingReinit) {
				this._pendingReinit = false;
				this.reinitIfNeeded(true);
			}
			return;
		}

		requestAnimationFrame(() => {
			const sliderConfig = this.getSliderConfig(displayType, mode);

			if (mode === 'mobile') {
				this.buildPages(this.getMobilePageSize(displayType));

				this.el._logosTns = tns({
					container: this.el,
					items: 1,
					slideBy: 1,
					autoplay: true,
					autoplayButtonOutput: false,
					mouseDrag: true,
					preventScrollOnTouch: 'auto',
					swipeAngle: false,
					speed: 400,
					controls: false,
					gutter: sliderConfig.gutter,
					nav: true,
					navPosition: 'bottom',
					loop: true,
					center: sliderConfig.center
				});

				this.el._logosLastOuter = this.el.closest('.tns-outer');

				this.el.style.visibility = prevVis || '';
				this._isReiniting = false;

				if (this._pendingReinit) {
					this._pendingReinit = false;
					this.reinitIfNeeded(true);
				}

				return;
			}

			if (displayType === 'slider_with_content') {
				this.buildPages(4);

				this.el._logosTns = tns({
					container: this.el,
					items: sliderConfig.items,
					slideBy: 1,
					autoplay: true,
					autoplayButtonOutput: false,
					mouseDrag: true,
					preventScrollOnTouch: 'auto',
					swipeAngle: false,
					speed: 400,
					controls: false,
					gutter: sliderConfig.gutter,
					nav: true,
					navPosition: 'bottom',
					loop: true,
					center: sliderConfig.center
				});

				this.el._logosLastOuter = this.el.closest('.tns-outer');

				this.el.style.visibility = prevVis || '';
				this._isReiniting = false;

				if (this._pendingReinit) {
					this._pendingReinit = false;
					this.reinitIfNeeded(true);
				}

				return;
			}

			this.el._logosTns = tns({
				container: this.el,
				responsive: sliderConfig.responsive,
				slideBy: 1,
				autoplay: true,
				autoplayButtonOutput: false,
				mouseDrag: true,
				preventScrollOnTouch: 'auto',
				swipeAngle: false,
				speed: 400,
				controls: false,
				gutter: sliderConfig.gutter,
				nav: true,
				navPosition: 'bottom',
				center: sliderConfig.center
			});

			this.el._logosLastOuter = this.el.closest('.tns-outer');

			this.el.style.visibility = prevVis || '';
			this._isReiniting = false;

			if (this._pendingReinit) {
				this._pendingReinit = false;
				this.reinitIfNeeded(true);
			}
		});
	}

	buildPages(perPage) {
		if (this.el.querySelector('.block-logos__page')) return;

		const slides = Array.from(this.el.children).filter(
			(n) => n.classList && n.classList.contains('block-logos__logo-slide')
		);

		if (!slides.length) return;

		const displayType = this.getDisplayType();

		const frag = document.createDocumentFragment();

		for (let i = 0; i < slides.length; i += perPage) {
			const page = document.createElement('div');
			page.className = 'block-logos__page';

			const grid = document.createElement('div');
			grid.className = 'block-logos__page-grid';

			if (displayType === 'slider') {
				grid.classList.add('block-logos__page-grid--slider');
			} else if (displayType === 'slider_three') {
				grid.classList.add('block-logos__page-grid--slider-three');
			} else if (displayType === 'slider_with_content') {
				grid.classList.add('block-logos__page-grid--slider-with-content');
			} else if (displayType === 'columns') {
				grid.classList.add('block-logos__page-grid--columns');
			}

			slides.slice(i, i + perPage).forEach((slide) => grid.appendChild(slide));
			page.appendChild(grid);
			frag.appendChild(page);
		}

		this.el.innerHTML = '';
		this.el.appendChild(frag);
	}
}

export default LogosSlider;